import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { StaffShell } from "@/components/shells";
import { Panel, Spinner, Btn, Tag, Select, Input, EmptyState } from "@/components/kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useAsync } from "@/lib/hooks";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import { loadStaffBookings, loadRefData, applyStatus, loadWriteContext, canWriteBooking } from "@/lib/staffData";
import { api } from "@/lib/api";
import { updateBucket, fmtDate, fmtDateTime, formatReg } from "@/lib/vda";

const ACTIONS = [
  { key: "garage_chased", label: "Garage chased", note: "Garage chased (bulk).", bumpDue: true },
  { key: "update_received", label: "Update received", note: "Update received (bulk).", bumpDue: true },
  { key: "work_in_progress", label: "Work in progress", internal: "work_in_progress" },
  { key: "parts_ordered", label: "Awaiting parts", internal: "parts_ordered" },
  { key: "awaiting_internal_authority", label: "Awaiting authority", internal: "awaiting_internal_authority" },
  { key: "vehicle_complete", label: "Ready for collection", internal: "vehicle_complete" },
  { key: "review_tomorrow", label: "No change, review tomorrow", note: "Reviewed — no change (bulk).", bumpDue: true },
  { key: "completed", label: "Complete booking", internal: "completed" },
  { key: "assign", label: "Assign AE" },
  { key: "set_due", label: "Set next update date" },
];

export default function BulkUpdate() {
  const { session, profile } = useAuth();
  const uid = session?.user?.id;
  const { data, loading, reload } = useAsync(async () => ({ bookings: await loadStaffBookings(), ref: await loadRefData(), writeCtx: await loadWriteContext(uid) }), [uid]);
  const [sel, setSel] = useState({});
  const [action, setAction] = useState("garage_chased");
  const [assignee, setAssignee] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const open = useMemo(() => (data?.bookings || []).filter((b) => b.internal_status !== "completed"), [data]);
  const selected = open.filter((b) => sel[b.id]);
  // Sprint 6: only rows the current user is authorised to write (assignee/cover/manage).
  const eligible = selected.filter((b) => data?.writeCtx && canWriteBooking(b, data.writeCtx));
  const ineligible = selected.filter((b) => !eligible.includes(b));
  const allChecked = open.length > 0 && selected.length === open.length;

  if (loading) return <StaffShell><Spinner /></StaffShell>;

  const toggleAll = () => {
    if (allChecked) setSel({});
    else setSel(Object.fromEntries(open.map((b) => [b.id, true])));
  };

  const apply = async () => {
    setBusy(true);
    const act = ACTIONS.find((a) => a.key === action);
    let done = 0;
    try {
      for (const b of eligible) {
        if (act.key === "assign") {
          await api.assignBooking(b.id, assignee);
        } else if (act.key === "set_due") {
          await applyStatus(b, { internal_note: `Next update date set (bulk).`, uid, source: "bulk", next_update_due_at: new Date(dueDate).toISOString() });
        } else if (act.internal) {
          await applyStatus(b, { internal_status: act.internal, internal_note: `${act.label} (bulk).`, uid, source: "bulk",
            next_update_due_at: dueDate ? new Date(dueDate).toISOString() : undefined });
        } else {
          await applyStatus(b, { internal_note: act.note, uid, source: "bulk",
            next_update_due_at: act.bumpDue ? new Date(dueDate ? dueDate : Date.now() + 24 * 3.6e6).toISOString() : undefined });
        }
        done++;
      }
      toast.success(`${done} booking${done === 1 ? "" : "s"} updated.${ineligible.length ? ` ${ineligible.length} skipped (need assignment/cover).` : ""}`);
      setSel({}); setConfirm(false); reload();
    } catch (e) {
      toast.error(e.message || "Bulk update failed");
    } finally { setBusy(false); }
  };

  const needAssignee = action === "assign";
  const needDue = action === "set_due";
  const canApply = selected.length > 0 && (!needAssignee || assignee) && (!needDue || dueDate);

  return (
    <StaffShell>
      <p className="vda-tick text-[hsl(var(--vda-clay))]">Bulk operations</p>
      <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-5">Bulk update open bookings</h1>

      <Panel className="p-3 mb-4 flex flex-wrap items-center gap-3">
        <span className="text-sm text-muted-foreground">{selected.length} selected</span>
        <Select value={action} onChange={(e) => setAction(e.target.value)} data-testid="bulk-action" className="w-auto min-w-[200px]">
          {ACTIONS.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
        </Select>
        {needAssignee && (
          <Select value={assignee} onChange={(e) => setAssignee(e.target.value)} data-testid="bulk-assignee" className="w-auto min-w-[180px]">
            <option value="">Choose AE…</option>
            {data.ref.staff.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
          </Select>
        )}
        {(needDue || ["garage_chased", "update_received", "review_tomorrow"].includes(action)) && (
          <Input type="datetime-local" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="bulk-due" className="w-auto" />
        )}
        <Btn data-testid="bulk-apply" onClick={() => setConfirm(true)} disabled={!canApply}>Review &amp; apply</Btn>
      </Panel>

      {open.length === 0 ? <EmptyState title="No open bookings" /> : (
        <Panel className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="bg-secondary/60 text-left vda-tick text-muted-foreground">
                  <th className="px-3 py-2"><input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="bulk-select-all" /></th>
                  <th className="px-3 py-2 font-medium">Reference</th>
                  <th className="px-3 py-2 font-medium">Reg</th>
                  <th className="px-3 py-2 font-medium">Customer</th>
                  <th className="px-3 py-2 font-medium">Garage</th>
                  <th className="px-3 py-2 font-medium">Appointment</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Next due</th>
                  <th className="px-3 py-2 font-medium">AE</th>
                </tr>
              </thead>
              <tbody>
                {open.map((b) => {
                  const bk = updateBucket(b.next_update_due_at);
                  return (
                    <tr key={b.id} className="border-t border-border hover:bg-secondary/40">
                      <td className="px-3 py-2"><input type="checkbox" checked={!!sel[b.id]} onChange={() => setSel((s) => ({ ...s, [b.id]: !s[b.id] }))} data-testid={`bulk-check-${b.human_reference}`} /></td>
                      <td className="px-3 py-2 vda-reg">{b.human_reference}</td>
                      <td className="px-3 py-2 vda-reg">{formatReg(b.vehicles?.registration)}</td>
                      <td className="px-3 py-2">{b.customers?.name}</td>
                      <td className="px-3 py-2 text-muted-foreground">{b.garage_name || "—"}</td>
                      <td className="px-3 py-2">{b.appointment_at ? fmtDate(b.appointment_at) : "—"}</td>
                      <td className="px-3 py-2"><Tag tone="muted">{b.public_status}</Tag></td>
                      <td className="px-3 py-2"><Tag tone={bk.tone}>{bk.label}</Tag></td>
                      <td className="px-3 py-2 text-muted-foreground">{b.assignee?.full_name || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {confirm && (
        <Dialog open={confirm} onOpenChange={(o) => { if (!o) setConfirm(false); }}>
          <DialogContent className="max-w-md max-h-[85vh] overflow-hidden flex flex-col" data-testid="bulk-confirm-modal">
            <DialogHeader>
              <DialogTitle>Confirm bulk update</DialogTitle>
              <DialogDescription>Review which selected bookings you can action before applying.</DialogDescription>
            </DialogHeader>
            <p className="text-sm">
              Apply <strong>{ACTIONS.find((a) => a.key === action).label}</strong> to the <strong>{eligible.length}</strong> booking{eligible.length === 1 ? "" : "s"} you can action.
            </p>
            {ineligible.length > 0 && (
              <div className="mt-2 rounded-[6px] bg-[hsl(var(--vda-clay))]/10 border border-[hsl(var(--vda-clay))]/30 p-2.5 text-xs" data-testid="bulk-ineligible">
                <strong>{ineligible.length}</strong> selected booking{ineligible.length === 1 ? "" : "s"} require assignment or cover and will be skipped:
                <div className="mt-1 vda-reg">{ineligible.map((b) => b.human_reference).join(", ")}</div>
              </div>
            )}
            <div className="mt-3 flex-1 min-h-0 overflow-auto text-xs vda-reg space-y-1 border-t border-border pt-3">
              {eligible.map((b) => <div key={b.id}>{b.human_reference} · {formatReg(b.vehicles?.registration)}</div>)}
            </div>
            <DialogFooter>
              <Btn variant="outline" onClick={() => setConfirm(false)}>Cancel</Btn>
              <Btn data-testid="bulk-confirm" onClick={apply} loading={busy} disabled={eligible.length === 0}>Apply to {eligible.length}</Btn>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </StaffShell>
  );
}
